import React, { useEffect, useMemo, useState } from 'react';
import { UploadCloud, FileIcon, Loader2 } from 'lucide-react';

// Shared by PrescriptionForm and ClaimForm.
// `accept` narrows the picker: photo slots take images only (the server rejects PDFs there).
export const FileField = ({ label, required, file, onChange, existingUrl, uploading, error, accept = 'image/*,.pdf', testId }) => {
    const isImage = file && file.type?.startsWith('image/');
    const previewUrl = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    return (
        <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
                {label}{required && <span className="text-red-500"> *</span>}
            </label>
            {file && (
                previewUrl ? (
                    <img src={previewUrl} alt={`${label} preview`} className="w-full max-h-40 object-contain rounded-lg border border-gray-200 mb-2 bg-gray-50" />
                ) : (
                    <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 mb-2 bg-gray-50">
                        <FileIcon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{file.name}</span>
                    </div>
                )
            )}
            <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-500 cursor-pointer hover:bg-gray-50">
                <UploadCloud className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                    {file ? file.name : existingUrl ? 'Replace file (currently uploaded)' : 'Choose file'}
                </span>
                <input type="file" accept={accept} className="hidden" data-testid={testId} aria-label={label} onChange={(e) => onChange(e.target.files?.[0] || null)} />
            </label>
            {uploading && (
                <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
                </p>
            )}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
    );
};

// Read-only field/file summaries shared by both forms' final review step.
export const ReviewField = ({ label, value }) => (
    <p><span className="text-gray-400">{label}:</span> <span className="font-medium text-gray-800 whitespace-pre-wrap">{value || '—'}</span></p>
);

export const ReviewFile = ({ label, file, existingUrl }) => {
    const isImage = file && file.type?.startsWith('image/');
    const previewUrl = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);
    const [existingImageFailed, setExistingImageFailed] = useState(false);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    return (
        <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">{label}</p>
            {previewUrl ? (
                <img src={previewUrl} alt={`${label} preview`} className="w-full max-h-40 object-contain rounded-lg border border-gray-200 bg-white" />
            ) : file ? (
                <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 bg-white">
                    <FileIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{file.name}</span>
                </div>
            ) : existingUrl && !existingImageFailed ? (
                <img
                    src={existingUrl}
                    alt={`${label} preview`}
                    className="w-full max-h-40 object-contain rounded-lg border border-gray-200 bg-white"
                    onError={() => setExistingImageFailed(true)}
                />
            ) : existingUrl ? (
                <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 bg-white">
                    <FileIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">Existing file on record</span>
                </div>
            ) : (
                <p className="text-sm text-gray-400">—</p>
            )}
        </div>
    );
};

export const TextField = ({ label, required, ...props }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
            {label}{required && <span className="text-red-500"> *</span>}
        </label>
        <input
            {...props}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
    </div>
);

// A dropdown styled like TextField. options: [{ value, label }].
export const SelectField = ({ label, required, options, placeholder = 'Select…', ...props }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
            {label}{required && <span className="text-red-500"> *</span>}
        </label>
        <select
            {...props}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
            <option value="">{placeholder}</option>
            {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    </div>
);
